import { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import PersonAddIcon from '@mui/icons-material/PersonAddAlt'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import type { CompteBoutique, RoleWithPermissions } from '@iobiz/shared'
import { useSessionStore } from '../../stores/sessionStore.js'
import { ConfirmDialog } from '../../ui/ConfirmDialog.js'
import { CompteBoutiqueDialog } from './CompteBoutiqueDialog.js'
import { envoyerInvitationCompte, majCompte, supprimerCompte } from './api.js'

/**
 * Les comptes logés dans le SCHÉMA de la boutique.
 *
 * ⭐ Le gérant en dispose entièrement : les créer, les modifier, poser un mot
 * de passe, désactiver, supprimer. Aucun garde-fou sur d'autres enseignes n'est
 * nécessaire — ces comptes n'existent que là, et c'est tout l'objet de les y
 * avoir mis.
 *
 * ⚠️ Ces personnes se connectent sur l'adresse de la BOUTIQUE, pas sur
 * l'accueil général : leur compte n'existe pas dans la table commune, et une
 * tentative depuis `/login` échouerait sans que rien ne l'explique. L'écran le
 * dit, sans quoi le gérant chercherait la panne ailleurs le premier jour.
 */
export function ListeComptes({
  storeId,
  comptes,
  roles,
  onChange,
  onErreur,
}: {
  storeId: string
  comptes: CompteBoutique[]
  roles: RoleWithPermissions[]
  onChange: () => Promise<void>
  onErreur: (m: string) => void
}): JSX.Element {
  const slug = useSessionStore((s) => s.stores.find((b) => b.storeId === storeId)?.slug)
  const moiId = useSessionStore((s) => s.user?.id)
  const [boite, setBoite] = useState<{ compte?: CompteBoutique } | null>(null)
  const [menu, setMenu] = useState<{ el: HTMLElement; compte: CompteBoutique } | null>(null)
  const [aSupprimer, setASupprimer] = useState<CompteBoutique | null>(null)
  const [lien, setLien] = useState<{ nom: string; envoye: boolean; url: string | null } | null>(
    null,
  )

  /**
   * ⭐ On n'envoie jamais de mot de passe : l'employé reçoit un lien et choisit
   * le sien. Le même geste sert à l'embauche et à l'oubli — d'où le libellé qui
   * change, mais pas la procédure.
   */
  async function envoyerLien(c: CompteBoutique): Promise<void> {
    try {
      const r = await envoyerInvitationCompte(
        storeId,
        c.id,
        c.status === 'invited' ? 'invitation' : 'reset',
      )
      setLien({ nom: c.displayName, ...r })
    } catch (e) {
      onErreur(e instanceof Error ? e.message : 'Envoi impossible.')
    }
  }

  async function agir(action: () => Promise<unknown>): Promise<void> {
    try {
      await action()
      await onChange()
    } catch (e) {
      onErreur(e instanceof Error ? e.message : 'Action impossible.')
    }
  }

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Typography variant="h3">Équipe</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Ces comptes n’existent que dans cette boutique. Connexion sur /s/{slug}/login
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setBoite({})}>
          Nouveau compte
        </Button>
      </Box>

      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        {comptes.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <Typography sx={{ color: 'text.secondary' }}>
              Aucun compte d’équipe. Créez-en un pour donner accès à la caisse.
            </Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Personne</TableCell>
                <TableCell>Adresse</TableCell>
                <TableCell>Rôle</TableCell>
                <TableCell>État</TableCell>
                <TableCell>Dernière connexion</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {comptes.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell sx={{ fontWeight: 500 }}>{c.displayName}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{c.email}</TableCell>
                  <TableCell>{c.roleName}</TableCell>
                  <TableCell>
                    {/* ⚠️ « Sans mot de passe » n'est pas décoratif : la personne
                        ne peut PAS se connecter tant qu'il n'y en a pas. Le dire
                        ici évite de chercher la panne ailleurs. */}
                    <Chip
                      size="small"
                      label={
                        c.status === 'active'
                          ? 'Actif'
                          : c.status === 'invited'
                            ? 'Sans mot de passe'
                            : 'Désactivé'
                      }
                      color={
                        c.status === 'active'
                          ? 'success'
                          : c.status === 'invited'
                            ? 'warning'
                            : 'default'
                      }
                      variant={c.status === 'active' ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                    {c.lastLoginAt ? new Date(c.lastLoginAt).toLocaleDateString('fr-FR') : 'jamais'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      aria-label={`Actions pour ${c.displayName}`}
                      onClick={(e) => setMenu({ el: e.currentTarget, compte: c })}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Menu anchorEl={menu?.el ?? null} open={Boolean(menu)} onClose={() => setMenu(null)}>
        <MenuItem
          onClick={() => {
            setBoite({ compte: menu!.compte })
            setMenu(null)
          }}
        >
          Modifier
        </MenuItem>

        <MenuItem
          onClick={() => {
            const c = menu!.compte
            setMenu(null)
            void envoyerLien(c)
          }}
        >
          {menu?.compte.status === 'invited'
            ? 'Envoyer l’invitation'
            : 'Envoyer un lien de mot de passe'}
        </MenuItem>

        {/* ⚠️ Rien sur son PROPRE compte : se désactiver depuis cet écran
            laisserait la boutique sans personne pour la gérer, et l'erreur ne
            se répare qu'en base. Le serveur le refuse également — un menu ne
            protège rien. */}
        {menu && menu.compte.id !== moiId && (
          <MenuItem
            onClick={() => {
              const c = menu.compte
              setMenu(null)
              void agir(() =>
                majCompte(storeId, c.id, {
                  status: c.status === 'disabled' ? 'active' : 'disabled',
                }),
              )
            }}
          >
            {menu.compte.status === 'disabled' ? 'Réactiver' : 'Désactiver'}
          </MenuItem>
        )}

        {menu && menu.compte.id !== moiId && (
          <MenuItem
            sx={{ color: 'error.main' }}
            onClick={() => {
              setASupprimer(menu.compte)
              setMenu(null)
            }}
          >
            Supprimer
          </MenuItem>
        )}
      </Menu>

      {boite && (
        <CompteBoutiqueDialog
          storeId={storeId}
          compte={boite.compte}
          roles={roles}
          onClose={() => setBoite(null)}
          onEnregistre={() => void onChange()}
        />
      )}

      {/* ⚠️ L'URL de secours s'affiche UNIQUEMENT si le courriel n'est pas
          parti : elle ouvre le compte à qui la lit. Mais une messagerie en
          panne ne doit pas laisser quelqu'un dehors sans recours — le gérant
          la transmet alors de la main à la main. */}
      <Dialog open={lien !== null} onClose={() => setLien(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{lien?.envoye ? 'Lien envoyé' : 'Courriel non parti'}</DialogTitle>
        <DialogContent>
          {lien?.envoye ? (
            <Typography>
              {lien.nom} a reçu un lien pour choisir son mot de passe. Il est valable une heure.
            </Typography>
          ) : (
            <>
              <Alert severity="warning" sx={{ mb: 3 }}>
                Le message n’a pas pu être envoyé. Transmettez ce lien à {lien?.nom} — il est
                valable une heure et ne sert qu’une fois.
              </Alert>
              <Typography
                variant="body2"
                sx={{ wordBreak: 'break-all', fontFamily: 'monospace', userSelect: 'all' }}
              >
                {lien?.url}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 6, py: 4 }}>
          <Button variant="contained" onClick={() => setLien(null)}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={aSupprimer !== null}
        title={`Supprimer le compte de « ${aSupprimer?.displayName ?? ''} » ?`}
        message="La personne ne pourra plus se connecter, et ses sessions ouvertes sont fermées. Les documents qu’elle a saisis restent inchangés."
        confirmLabel="Supprimer"
        onClose={() => setASupprimer(null)}
        onConfirm={async () => {
          const c = aSupprimer
          setASupprimer(null)
          if (c) await agir(() => supprimerCompte(storeId, c.id))
        }}
      />
    </>
  )
}
